import { type TFunction } from "i18next";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RemoveCommodityDialogProps {
  commodityName?: string;
  countryName?: string;
  farmersCount: number;
  inUse?: boolean;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: TFunction;
}

// Shown before a commodity is removed from the current country. When the commodity is in
// use (deposits/offers) the backend would reject the deactivation, so instead of a confirm
// prompt the modal becomes the same "unable to deactivate" message as the top-of-page
// alert, with no confirm action. Otherwise it confirms the removal and surfaces how many
// farmers are still linked so the manager understands the impact.
export const RemoveCommodityDialog = ({
  commodityName,
  countryName,
  farmersCount,
  inUse = false,
  open,
  onCancel,
  onConfirm,
  t,
}: RemoveCommodityDialogProps) => (
  <Dialog open={open} onOpenChange={nextOpen => (nextOpen ? undefined : onCancel())}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="mt-2 pr-8">
          {inUse
            ? t("commodities.deactivateError.title", {
                commodity: commodityName,
                country: countryName,
              })
            : t("commodities.removeConfirm.title", {
                commodity: commodityName,
                country: countryName,
              })}
        </DialogTitle>
        <DialogDescription>
          {inUse
            ? t("commodities.deactivateError.body")
            : t("commodities.removeConfirm.body", { count: farmersCount, country: countryName })}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <WFPButton type="button" variant="outline" onClick={onCancel}>
          {t("commodities.removeConfirm.cancel")}
        </WFPButton>
        {/* Hidden when the commodity is in use: the backend would reject the
            deactivation, so offering "Confirm" would only produce an error. */}
        {!inUse && (
          <WFPButton type="button" variant="destructive" onClick={onConfirm}>
            {t("commodities.removeConfirm.confirm")}
          </WFPButton>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
